import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  ArrowLeft, BookOpen, GraduationCap, Lightbulb, ChevronRight,
  Palette, Heart, Brain, Scale, Building, Eye, Gavel, MessageSquare,
  Bird, TreePine, Cog, Cross, FlaskConical, Cpu, Clock, Hammer, Search
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icons for each BAC notion
const NOTION_ICONS = {
  "L'art": Palette,
  "Le bonheur": Heart,
  "La conscience": Brain,
  "Le devoir": Scale,
  "L'Etat": Building,
  "L'inconscient": Eye,
  "La justice": Gavel,
  "Le langage": MessageSquare,
  "La liberte": Bird,
  "La nature": TreePine,
  "La raison": Cog,
  "La religion": Cross,
  "La science": FlaskConical,
  "La technique": Cpu,
  "Le temps": Clock,
  "Le travail": Hammer,
  "La vérité": Search
};

// Colors for each notion
const NOTION_COLORS = {
  "L'art": "from-pink-500 to-rose-600",
  "Le bonheur": "from-amber-400 to-orange-500",
  "La conscience": "from-purple-500 to-indigo-600",
  "Le devoir": "from-slate-500 to-slate-700",
  "L'Etat": "from-blue-600 to-blue-800",
  "L'inconscient": "from-violet-500 to-purple-700",
  "La justice": "from-emerald-500 to-teal-600",
  "Le langage": "from-cyan-400 to-blue-500",
  "La liberte": "from-sky-400 to-cyan-600",
  "La nature": "from-green-500 to-emerald-600",
  "La raison": "from-gray-500 to-slate-600",
  "La religion": "from-amber-600 to-yellow-700",
  "La science": "from-indigo-500 to-blue-600",
  "La technique": "from-zinc-500 to-gray-700",
  "Le temps": "from-orange-500 to-red-500",
  "Le travail": "from-stone-500 to-stone-700",
  "La vérité": "from-teal-500 to-cyan-600"
};

const ThematiquesPage = ({ user }) => {
  const navigate = useNavigate();
  const [notions, setNotions] = useState([]);
  const [selectedNotion, setSelectedNotion] = useState(null);
  const [philosophers, setPhilosophers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPhilosophers, setLoadingPhilosophers] = useState(false);

  useEffect(() => {
    fetchNotions();
  }, []);

  const fetchNotions = async () => {
    try {
      const response = await axios.get(`${API}/bac-notions`);
      setNotions(response.data.notions || []);
    } catch (error) {
      console.error("Error fetching notions:", error);
      // Fallback to hardcoded list
      setNotions([
        "L'art", "Le bonheur", "La conscience", "Le devoir", "L'État",
        "L'inconscient", "La justice", "Le langage", "La liberté", "La nature",
        "La raison", "La religion", "La science", "La technique", "Le temps",
        "Le travail", "La vérité"
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhilosophersForNotion = async (notion) => {
    setLoadingPhilosophers(true);
    setSelectedNotion(notion);
    try {
      const response = await axios.get(`${API}/bac-notions/${encodeURIComponent(notion)}/philosophers`);
      setPhilosophers(response.data.philosophers || []);
    } catch (error) {
      console.error("Error fetching philosophers:", error);
      toast.error("Erreur lors du chargement des philosophes");
    } finally {
      setLoadingPhilosophers(false);
    }
  };

  const getIcon = (notion) => {
    const IconComponent = NOTION_ICONS[notion] || BookOpen;
    return <IconComponent className="w-6 h-6" />;
  };

  const getGradient = (notion) => {
    return NOTION_COLORS[notion] || "from-gray-500 to-gray-700";
  };

  // Main view - all notions
  if (!selectedNotion) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]" data-testid="thematiques-page">
        {/* Header */}
        <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>
            <span className="navbar-logo hidden sm:block">SophIA</span>
            <Badge className="ml-2 bg-[#D4AF37] text-[#0F172A]">
              <GraduationCap className="w-3 h-3 mr-1" />
              Thématiques Bac
            </Badge>
          </div>
        </nav>

        <main className="px-4 md:px-8 py-6 max-w-7xl mx-auto page-enter">
          <div className="mb-8 text-center">
            <h1 className="text-3xl md:text-4xl text-[#1A365D] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Les 17 Notions du Baccalauréat
            </h1>
            <p className="text-[#64748B] max-w-2xl mx-auto">
              Explorez chaque thème et découvrez ce que les grands philosophes en pensent.
              Idéal pour préparer vos dissertations et commentaires !
            </p>
          </div>

          {/* Notions Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {notions.map((notion, index) => (
              <Card
                key={index}
                className={`cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-xl overflow-hidden`}
                onClick={() => fetchPhilosophersForNotion(notion)}
                data-testid={`notion-card-${notion.replace(/\s/g, '-').toLowerCase()}`}
              >
                <div className={`bg-gradient-to-br ${getGradient(notion)} p-6 text-white`}>
                  <div className="flex items-center justify-between mb-3">
                    {getIcon(notion)}
                    <ChevronRight className="w-5 h-5 opacity-70" />
                  </div>
                  <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {notion}
                  </h3>
                  <p className="text-xs mt-1 opacity-80">
                    Cliquez pour voir les positions
                  </p>
                </div>
              </Card>
            ))}
          </div>

          {/* Quick Tips */}
          <Card className="mt-8 card-sjh bg-gradient-to-r from-[#F8F9FA] to-white">
            <CardHeader>
              <CardTitle className="text-[#1A365D] flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#D4AF37]" />
                Conseil pour le Bac
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-[#64748B]">
                Pour chaque notion, essayez de connaître au moins <strong>3 philosophes</strong> avec des positions
                <strong> différentes</strong>. Cela vous permettra de construire une dissertation équilibrée
                avec thèse, antithèse et synthèse.
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Detail view - philosophers for selected notion
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="thematique-detail-page">
      {/* Header */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedNotion(null)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Thématiques
          </Button>
          <span className="navbar-logo hidden sm:block">SophIA</span>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 max-w-5xl mx-auto page-enter">
        {/* Notion Header */}
        <div className={`rounded-xl bg-gradient-to-br ${getGradient(selectedNotion)} p-8 text-white mb-8`}>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              {getIcon(selectedNotion)}
            </div>
            <div>
              <Badge className="bg-white/20 text-white mb-2">Notion du Programme</Badge>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
                {selectedNotion}
              </h1>
            </div>
          </div>
          <p className="text-white/90 text-lg">
            Découvrez ce que les philosophes pensent de cette notion fondamentale du programme.
          </p>
        </div>

        {/* Philosophers List */}
        {loadingPhilosophers ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-[#64748B]">Chargement des philosophes...</div>
          </div>
        ) : philosophers.length > 0 ? (
          <div className="space-y-4">
            <h2 className="text-xl text-[#1A365D] font-semibold mb-4">
              {philosophers.length} philosophes ont une position sur {selectedNotion}
            </h2>
            
            {philosophers.map((phil, index) => (
              <Card 
                key={index} 
                className="card-sjh hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/library/${phil.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {phil.avatar ? (
                      <img 
                        src={phil.avatar} 
                        alt={phil.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-[#D4AF37]"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-[#1A365D] flex items-center justify-center text-white text-xl font-bold">
                        {phil.name.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-semibold text-[#1A365D]">{phil.name}</h3>
                        <Badge variant="outline" className="text-xs">{phil.era}</Badge>
                        {phil.tradition === "chretien" && (
                          <Badge className="bg-[#D4AF37] text-[#0F172A] text-xs">
                            <Cross className="w-3 h-3 mr-1" />
                            Chretien
                          </Badge>
                        )}
                      </div>
                      <div className="p-4 bg-[#F8F9FA] rounded-lg mt-3 border-l-4 border-[#D4AF37]">
                        <p className="text-[#0F172A] leading-relaxed italic">
                          "{phil.position}"
                        </p>
                      </div>
                      <Button 
                        variant="link" 
                        className="text-[#1A365D] p-0 mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/library/${phil.id}`);
                        }}
                      >
                        Voir le profil complet →
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-sjh">
            <CardContent className="p-8 text-center">
              <GraduationCap className="w-12 h-12 mx-auto text-[#64748B] mb-4" />
              <p className="text-[#64748B]">
                Aucun philosophe avec une position sur cette notion pour le moment.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Use in Dissertation Tips */}
        <Card className="mt-8 card-sjh border-[#D4AF37]">
          <CardHeader>
            <CardTitle className="text-[#1A365D] flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-[#D4AF37]" />
              Comment utiliser ces positions ?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge className="bg-[#22C55E] text-white mt-1">1</Badge>
              <p className="text-[#64748B]">
                <strong>Thèse :</strong> Choisissez un philosophe qui défend une position claire
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-[#F59E0B] text-white mt-1">2</Badge>
              <p className="text-[#64748B]">
                <strong>Antithèse :</strong> Trouvez un philosophe avec une position opposée ou nuancée
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-[#3B82F6] text-white mt-1">3</Badge>
              <p className="text-[#64748B]">
                <strong>Synthèse :</strong> Proposez une position qui dépasse l'opposition
              </p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ThematiquesPage;
